import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";
import { t } from "ttag";

import { submitUploadedDocuments } from "@/app/api/legacy-api";
import { LogoLoader } from "@/shared/components";
import type { UploadFilesValues } from "@/shared/models/legacy-types";
import {
  Button,
  Dialog,
  errorToast,
  IconWrapper,
  Spinner,
  useToastStore,
} from "@/shared/ui";
import { tw } from "@/shared/utils";
import { getDocumentLabel } from "../components.constants";

interface DocumentUploadsSubmitProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const DocumentUploadsSubmit = ({
  onCancel,
  onSuccess,
}: DocumentUploadsSubmitProps) => {
  const { pushToast } = useToastStore();
  const {
    handleSubmit,
    formState: { errors, isDirty, isValidating },
    control,
  } = useFormContext<UploadFilesValues>();
  const folderName = useWatch({ control, name: `type` });

  const queryClient = useQueryClient();
  const { mutate: submitDocumentsMutation, isPending } = useMutation({
    mutationFn: submitUploadedDocuments.mutation,
    onSuccess: () => {
      onSuccess();
      submitUploadedDocuments.invalidates(queryClient);
      void pushToast({
        type: "success",
        title: t`Success`,
        message: t`Document successfully uploaded!`,
      });
    },
    onError: errorToast,
  });

  const handleSave = handleSubmit((values) => {
    submitDocumentsMutation(values);
  });

  const hasUploadErrors = Boolean(errors.files);
  const canSubmit = !hasUploadErrors && isDirty && !isValidating && !isPending;

  const documentLabel = getDocumentLabel(folderName);

  return (
    <Dialog.Footer>
      <Button onClick={onCancel} size="lg" variant="secondary">
        {t`Cancel`}
      </Button>

      <Button
        size="lg"
        disabled={!canSubmit}
        onClick={handleSave}
        className={tw("relative", isPending && "text-transparent")}
      >
        {t`Save ${documentLabel}`}
        {isPending && (
          <IconWrapper className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
            <Spinner variant="dark" />
          </IconWrapper>
        )}
      </Button>

      <LogoLoader show={isPending} fullscreen />
    </Dialog.Footer>
  );
};
